/**
 * User Growth Page
 * Operational dashboard: registrations, activity, real-vs-team, platform, retention.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ComposedChart,
  AreaChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, UserPlus, Users, Repeat, Smartphone, Apple, HelpCircle } from 'lucide-react';
import { Card, Checkbox } from '@/components/ui';
import { Loading } from '@/components/common/Loading';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import { userGrowthApi, type DailyGrowthRow, type RangeParams } from '@/lib/api/userGrowth';

const RANGE_PRESETS = [7, 14, 30, 90] as const;
type RangePreset = (typeof RANGE_PRESETS)[number];

function KpiCard({
  label,
  value,
  icon: Icon,
  color,
  bg,
}: {
  label: string;
  value: string;
  icon: typeof UserPlus;
  color: string;
  bg: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bg}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function classificationBadge(cls: string, labels: { admin: string; internalTester: string; regularUser: string }) {
  switch (cls) {
    case 'admin':
      return { text: labels.admin, cls: 'bg-purple-50 text-purple-700 border-purple-200' };
    case 'internal_tester':
      return { text: labels.internalTester, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    default:
      return { text: labels.regularUser, cls: 'bg-green-50 text-green-700 border-green-200' };
  }
}

function PlatformIcon({ platform }: { platform: string | null }) {
  if (platform === 'ios') return <Apple className="w-4 h-4 text-gray-600" />;
  if (platform === 'android') return <Smartphone className="w-4 h-4 text-green-600" />;
  return <HelpCircle className="w-4 h-4 text-gray-300" />;
}

function formatDateTime(ts: string | null): string {
  if (!ts) return '-';
  return parseBackendDateTime(ts).toLocaleDateString();
}

export function UserGrowthPage() {
  const { messages } = useI18n();
  const t = messages.userGrowth;

  const [preset, setPreset] = useState<RangePreset>(14);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [excludeTeam, setExcludeTeam] = useState(true);

  // Pre-validate the custom range; invalid combinations never hit the API.
  // Date.parse on YYYY-MM-DD is UTC for both ends, so the day diff is exact.
  const rangeInvalid = useMemo(() => {
    if (!customStart || !customEnd) return false;
    const startMs = Date.parse(customStart);
    const endMs = Date.parse(customEnd);
    return endMs < startMs || (endMs - startMs) / 86_400_000 + 1 > 180;
  }, [customStart, customEnd]);

  // A complete, valid custom range overrides the preset; picking a preset clears it.
  const isCustom = Boolean(customStart && customEnd) && !rangeInvalid;
  const range: RangeParams = useMemo(
    () => (isCustom ? { start_date: customStart, end_date: customEnd } : { days: preset }),
    [isCustom, customStart, customEnd, preset],
  );

  const selectPreset = (d: RangePreset) => {
    setPreset(d);
    setCustomStart('');
    setCustomEnd('');
  };

  const summaryQ = useQuery({
    queryKey: ['user-growth-summary'],
    queryFn: () => userGrowthApi.getSummary(),
  });

  const dailyQ = useQuery({
    queryKey: ['user-growth-daily', range],
    queryFn: () => userGrowthApi.getDaily(range),
  });

  const activeQ = useQuery({
    queryKey: ['user-growth-active', range, excludeTeam],
    queryFn: () => userGrowthApi.getActiveUsers({ ...range, exclude_team: excludeTeam }),
  });

  const funnelQ = useQuery({
    queryKey: ['user-growth-funnel', range],
    queryFn: () => userGrowthApi.getFunnel(range),
  });

  // Fixed-definition KPI metrics: deliberately not range-driven.
  const retentionQ = useQuery({
    queryKey: ['user-growth-retention'],
    queryFn: () => userGrowthApi.getRetention(),
  });

  const chartData = useMemo(() => {
    const rows = dailyQ.data ?? [];
    // MM-DD ticks are ambiguous when the window crosses a year boundary;
    // switch to YY-MM-DD in that case (tooltip shows the same label).
    const crossYear =
      rows.length > 1 && rows[0].date.slice(0, 4) !== rows[rows.length - 1].date.slice(0, 4);
    return rows.map((r: DailyGrowthRow) => ({
      ...r,
      date: crossYear ? r.date.slice(2) : r.date.slice(5),
    }));
  }, [dailyQ.data]);

  const summary = summaryQ.data;
  const d1 = summary?.d1_retention_7d;

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
          <p className="text-sm text-gray-500 mt-1">{t.pageDescription}</p>
        </div>
        {/* Shared time-range control: presets + custom start/end */}
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {RANGE_PRESETS.map((d) => (
                <button
                  key={d}
                  onClick={() => selectPreset(d)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    !isCustom && preset === d
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {d === 7 ? t.range.days7 : d === 14 ? t.range.days14 : d === 30 ? t.range.days30 : t.range.days90}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                aria-label={t.range.startDate}
                title={t.range.startDate}
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-gray-400 text-sm">–</span>
              <input
                type="date"
                aria-label={t.range.endDate}
                title={t.range.endDate}
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {rangeInvalid && <p className="text-xs text-red-600">{t.range.invalidRange}</p>}
        </div>
      </div>

      {/* KPI cards */}
      {summaryQ.isLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 h-20 animate-pulse" />
          ))}
        </div>
      ) : summaryQ.isError ? (
        <ErrorMessage error={t.error} onRetry={() => summaryQ.refetch()} />
      ) : (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard
            label={t.kpi.todayNewUsers}
            value={String(summary?.today_new_users ?? 0)}
            icon={UserPlus}
            color="text-blue-600"
            bg="bg-blue-50"
          />
          <KpiCard
            label={t.kpi.todayActive}
            value={`${summary?.today_active_regular ?? 0} / ${summary?.today_active_total ?? 0} ${t.kpi.todayActiveSuffix}`}
            icon={Users}
            color="text-green-600"
            bg="bg-green-50"
          />
          <KpiCard
            label={t.kpi.newUsers7d}
            value={String(summary?.new_users_7d ?? 0)}
            icon={TrendingUp}
            color="text-indigo-600"
            bg="bg-indigo-50"
          />
          <KpiCard
            label={t.kpi.d1Retention}
            value={d1 === null || d1 === undefined ? '—' : `${(d1 * 100).toFixed(1)}%`}
            icon={Repeat}
            color="text-amber-600"
            bg="bg-amber-50"
          />
        </div>
      )}

      {/* Trend chart */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">{t.trend.title}</h3>
        </div>
        {dailyQ.isLoading ? (
          <div className="h-72 flex items-center justify-center">
            <Loading message={t.loading} />
          </div>
        ) : dailyQ.isError ? (
          <ErrorMessage error={t.error} onRetry={() => dailyQ.refetch()} />
        ) : !chartData.length ? (
          <div className="h-72 flex items-center justify-center text-gray-400">{t.table.empty}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: 13,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="new_users_ios" stackId="new" name={t.trend.newUsersIos} fill="#3b82f6" />
              <Bar dataKey="new_users_android" stackId="new" name={t.trend.newUsersAndroid} fill="#22c55e" />
              <Bar dataKey="new_users_unknown" stackId="new" name={t.trend.newUsersUnknown} fill="#d1d5db" />
              <Line
                type="monotone"
                dataKey="active_regular"
                name={t.trend.activeRegular}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="active_team"
                name={t.trend.activeTeam}
                stroke="#a855f7"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Cumulative user growth */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">{t.cumulative.title}</h3>
        </div>
        {dailyQ.isLoading ? (
          <div className="h-64 flex items-center justify-center">
            <Loading message={t.loading} />
          </div>
        ) : dailyQ.isError ? (
          <ErrorMessage error={t.error} onRetry={() => dailyQ.refetch()} />
        ) : !chartData.length ? (
          <div className="h-64 flex items-center justify-center text-gray-400">{t.table.empty}</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="cumRegularGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: 13,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="cumulative_regular"
                name={t.cumulative.regular}
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#cumRegularGrad)"
              />
              <Area
                type="monotone"
                dataKey="cumulative_total"
                name={t.cumulative.total}
                stroke="#9ca3af"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fillOpacity={0}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Daily active detail table */}
      <Card padding={false}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">{t.table.title}</h3>
          <div className="shrink-0">
            <Checkbox
              label={t.table.excludeTeam}
              checked={excludeTeam}
              onChange={(e) => setExcludeTeam(e.target.checked)}
            />
          </div>
        </div>
        {activeQ.isLoading ? (
          <div className="p-8 flex justify-center">
            <Loading message={t.loading} />
          </div>
        ) : activeQ.isError ? (
          <div className="p-4">
            <ErrorMessage error={t.error} onRetry={() => activeQ.refetch()} />
          </div>
        ) : !activeQ.data?.length ? (
          <div className="p-8 text-center text-gray-400">{t.table.empty}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-2">{t.table.colDate}</th>
                  <th className="px-4 py-2">{t.table.colUser}</th>
                  <th className="px-4 py-2">{t.table.colClassification}</th>
                  <th className="px-4 py-2">{t.table.colRegistered}</th>
                  <th className="px-4 py-2 text-center">{t.table.colPlatform}</th>
                  <th className="px-4 py-2 text-right">{t.table.colSessions}</th>
                  <th className="px-4 py-2 text-right">{t.table.colRuns}</th>
                </tr>
              </thead>
              <tbody>
                {activeQ.data.map((row, idx) => {
                  const badge = classificationBadge(row.classification, t.classification);
                  return (
                    <tr key={`${row.date}-${row.user_id}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{row.date}</td>
                      <td className="px-4 py-2.5">
                        <div className="truncate max-w-[180px] text-gray-900" title={row.user_id}>
                          {row.display_name}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-md border text-xs font-medium ${badge.cls}`}>
                          {badge.text}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                          {formatDateTime(row.registered_at)}
                          {row.is_new_user && (
                            <span className="inline-flex px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 text-[11px] font-medium">
                              {t.table.newBadge}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-center">
                          <PlatformIcon platform={row.platform} />
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700 font-mono">{row.sessions}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700 font-mono">{row.runs}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Retention */}
      <Card>
        <h3 className="text-sm font-medium text-gray-700 mb-4">{t.retention.title}</h3>
        {retentionQ.isLoading ? (
          <div className="h-32 flex items-center justify-center">
            <Loading message={t.loading} />
          </div>
        ) : retentionQ.isError ? (
          <ErrorMessage error={t.error} onRetry={() => retentionQ.refetch()} />
        ) : (
          <div className="flex gap-6 flex-wrap lg:flex-nowrap">
            <div className="flex flex-row lg:flex-col gap-3 shrink-0">
              {[
                { label: t.retention.d7, value: retentionQ.data?.d7 },
                { label: t.retention.d30, value: retentionQ.data?.d30 },
                { label: t.retention.dauMau, value: retentionQ.data?.dau_mau },
              ].map((kpi) => (
                <div key={kpi.label} className="rounded-lg border border-gray-200 px-4 py-3 min-w-[140px]">
                  <p className="text-xs text-gray-500">{kpi.label}</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {kpi.value === null || kpi.value === undefined ? '—' : `${(kpi.value * 100).toFixed(1)}%`}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex-1 overflow-x-auto">
              <p className="text-xs text-gray-500 mb-2">{t.retention.cohortTitle}</p>
              <CohortTriangle
                cohorts={retentionQ.data?.cohorts ?? []}
                weekPrefix={t.retention.weekPrefix}
                sizeSuffix={t.retention.cohortSizeSuffix}
              />
            </div>
          </div>
        )}
      </Card>

      {/* Funnel */}
      <Card>
        <h3 className="text-sm font-medium text-gray-700 mb-4">{t.funnel.title}</h3>
        {funnelQ.isLoading ? (
          <div className="h-24 flex items-center justify-center">
            <Loading message={t.loading} />
          </div>
        ) : funnelQ.isError ? (
          <ErrorMessage error={t.error} onRetry={() => funnelQ.refetch()} />
        ) : (
          <>
            <FunnelRow
              steps={[
                { label: t.funnel.registered, value: funnelQ.data?.registered ?? 0 },
                { label: t.funnel.createdSelfChart, value: funnelQ.data?.created_self_chart ?? 0 },
                { label: t.funnel.firstConversation, value: funnelQ.data?.first_conversation ?? 0 },
                { label: t.funnel.trialStarted, value: funnelQ.data?.trial_started ?? 0 },
                { label: t.funnel.paid, value: funnelQ.data?.paid ?? 0 },
              ]}
            />
            {funnelQ.data?.median_hours_to_chart != null && (
              <p className="text-xs text-gray-500 mt-3">
                {t.funnel.medianHoursToChart}: {funnelQ.data.median_hours_to_chart} {t.funnel.hoursUnit}
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

const COHORT_BASE_RGB = '59, 130, 246'; // blue-500, matches the page's primary chart color

function CohortTriangle({
  cohorts,
  weekPrefix,
  sizeSuffix,
}: {
  cohorts: { week_start: string; cohort_size: number; retention: number[] }[];
  weekPrefix: string;
  sizeSuffix: string;
}) {
  const maxWeeks = Math.max(0, ...cohorts.map((c) => c.retention.length));
  return (
    <table className="text-xs border-separate" style={{ borderSpacing: 2 }}>
      <thead>
        <tr className="text-gray-500">
          <th className="pr-2 text-left font-normal" />
          {Array.from({ length: maxWeeks }, (_, i) => (
            <th key={i} className="px-1 font-normal w-12 text-center">
              {weekPrefix}
              {i}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cohorts.map((c) => (
          <tr key={c.week_start}>
            <td className="pr-2 whitespace-nowrap text-gray-600">
              {c.week_start}
              <span className="text-gray-400 ml-1">
                {c.cohort_size}
                {sizeSuffix}
              </span>
            </td>
            {c.retention.map((v, i) => (
              <td
                key={i}
                className="w-12 h-7 text-center rounded"
                style={{
                  backgroundColor: `rgba(${COHORT_BASE_RGB}, ${v})`,
                  color: v > 0.5 ? '#fff' : '#374151',
                }}
              >
                {(v * 100).toFixed(0)}%
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FunnelRow({ steps }: { steps: { label: string; value: number }[] }) {
  const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-fuchsia-500', 'bg-pink-500'];
  return (
    <div className="flex items-stretch gap-2">
      {steps.map((step, i) => {
        const prev = i > 0 ? steps[i - 1].value : null;
        const conv = prev && prev > 0 ? ((step.value / prev) * 100).toFixed(0) + '%' : null;
        return (
          <div key={step.label} className="flex items-center gap-2 flex-1">
            <div className={`flex-1 rounded-lg ${colors[i]} text-white p-4 text-center`}>
              <div className="text-2xl font-bold">{step.value}</div>
              <div className="text-xs opacity-90 mt-1">{step.label}</div>
            </div>
            {i < steps.length - 1 && (
              <div className="text-xs text-gray-400 whitespace-nowrap w-12 text-center">{conv ?? '→'}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
